import { useEffect } from "react"

import { usePlayerStore } from "../stores/usePlayerStore"

// 2026-05-05 v7 — Drag de la cámara en primera persona.
//
// Cuando el Tripulante está en FP, mantener el botón presionado
// mientras se mueve el dedo/cursor rota la cámara: yaw cambia con el
// delta horizontal, pitch con el delta vertical. El umbral
// `DRAG_ACTIVATE_PX` evita que un tap accidental sobre un avatar
// remoto (que ya tiene su propio handler de hold para iniciar
// enlace) o sobre el suelo (MovementPlane) dispare la rotación —
// solo cuenta como drag si el dedo se aleja más de ese umbral del
// punto de inicio.
//
// La cámara y el avatar comparten yaw en FP: cuando el cuerpo
// camina hacia un target, LocalAvatar reescribe cameraYaw para que
// la cámara herede la dirección. Cuando el cuerpo está quieto y el
// Tripulante hace drag, el avatar copia el yaw nuevo (rota en
// idle hacia donde mira la cámara).
//
// El listener vive a nivel `window` para no chocar con los handlers
// R3F de los avatares y del MovementPlane: aquellos usan
// `e.stopPropagation()` que solo detiene la propagación dentro de
// R3F, no el evento DOM original. Así, el drag de cámara coexiste
// con tap-to-move y hold-para-enlace.

const DRAG_ACTIVATE_PX = 8
const YAW_SENSITIVITY = 0.0048
const PITCH_SENSITIVITY = 0.0042

export function useFpCameraInput(): void {
    const cameraMode = usePlayerStore((s) => s.cameraMode)

    useEffect(() => {
        if (cameraMode !== "firstPerson") return

        let down = false
        let dragActivated = false
        let downX = 0
        let downY = 0
        let lastX = 0
        let lastY = 0
        let pointerId: number | null = null

        const onDown = (e: PointerEvent) => {
            // Solo el botón principal del mouse, o cualquier touch.
            if (e.pointerType === "mouse" && e.button !== 0) return
            // Si ya hay un pointer activo (segundo dedo) ignoramos —
            // pinch-zoom queda para OrbitControls (que está apagado en
            // FP, así que el segundo dedo no hace nada en este modo).
            if (down) return
            down = true
            dragActivated = false
            pointerId = e.pointerId
            downX = e.clientX
            downY = e.clientY
            lastX = e.clientX
            lastY = e.clientY
        }

        const onMove = (e: PointerEvent) => {
            if (!down) return
            if (pointerId != null && e.pointerId !== pointerId) return

            if (!dragActivated) {
                const totalDx = e.clientX - downX
                const totalDy = e.clientY - downY
                if (
                    totalDx * totalDx + totalDy * totalDy <
                    DRAG_ACTIVATE_PX * DRAG_ACTIVATE_PX
                ) {
                    return
                }
                dragActivated = true
                lastX = e.clientX
                lastY = e.clientY
            }

            const dx = e.clientX - lastX
            const dy = e.clientY - lastY
            lastX = e.clientX
            lastY = e.clientY

            const state = usePlayerStore.getState()
            // dx>0 (dedo hacia la derecha) → cámara mira a la izquierda
            //   relativo a sí misma, por lo que el yaw decrece.
            // dy>0 (dedo hacia abajo) → cámara mira hacia abajo, pitch
            //   negativo. Invertimos signo de dy para que arrastrar
            //   hacia abajo baje la mirada.
            const newYaw = state.cameraYaw - dx * YAW_SENSITIVITY
            const newPitch = state.cameraPitch - dy * PITCH_SENSITIVITY
            state.setCameraOrientation(newYaw, newPitch)
        }

        const onUp = (e: PointerEvent) => {
            if (pointerId != null && e.pointerId !== pointerId) return
            down = false
            dragActivated = false
            pointerId = null
        }

        const onBlur = () => {
            down = false
            dragActivated = false
            pointerId = null
        }

        window.addEventListener("pointerdown", onDown)
        window.addEventListener("pointermove", onMove)
        window.addEventListener("pointerup", onUp)
        window.addEventListener("pointercancel", onUp)
        window.addEventListener("blur", onBlur)

        return () => {
            window.removeEventListener("pointerdown", onDown)
            window.removeEventListener("pointermove", onMove)
            window.removeEventListener("pointerup", onUp)
            window.removeEventListener("pointercancel", onUp)
            window.removeEventListener("blur", onBlur)
        }
    }, [cameraMode])
}
